import { Button, Card, Col, Divider, Form, Input, Row, Select, Switch, Typography, Space, Tooltip } from "antd";
import { IInstitution, InstitutionAPI, InstitutionWebhook, updateInstitution } from "../../redux/reducer/institution";
import { useCallback, useState } from "react";
import { getSignatureKey } from "../../utils";
import Copy from "../atoms/Copy";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../configureStore";
import { useMsal } from "@azure/msal-react";
import {
    DeleteOutlined,
    PlusOutlined,
    SaveOutlined,
    UndoOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

interface Params {
    type: 'webhook' | 'api'
    params: { hooks: InstitutionWebhook[] } | InstitutionAPI;
    institution: IInstitution;
}

interface WebhookParams extends Params {
    type: 'webhook';
    params: { hooks: InstitutionWebhook[]; };
}

interface ApiParams extends Params {
    type: 'api';
    params: InstitutionAPI;
}

type ConfigurationFormCardProps = WebhookParams | ApiParams;

const ConfigurationFormCard = ({
    type,
    params,
    institution,
}: ConfigurationFormCardProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const loading = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['institution/PATCH_INSTITUTION']);
    const { accounts } = useMsal();

    const [form] = Form.useForm<typeof params>();
    const [disabled, setDisabled] = useState<boolean>(type === 'api' ? !params.enabled : false);

    const onFinish = useCallback((values: typeof params) => {
        const update = structuredClone(institution);
        if (type === 'api') {
            update.api = values as InstitutionAPI;
        } else if (type === 'webhook') {
            update.webhooks = (values as { hooks: InstitutionWebhook[] }).hooks;
        }

        dispatch(updateInstitution({
            input: update,
            message: { success: `Successfully updated institution ${type}` },
            headers: {
                Authorization: `Bearer ${accounts[0].idToken}`,
            },
        }))
    }, [institution, accounts]);

    return (
        <Form
            form={form}
            initialValues={params}
            onFinish={onFinish}
            layout="vertical"
        >
            <Card
                styles={{ body: { padding: '16px' } }}
                extra={(type === 'api') ? (
                    <Form.Item
                        name='enabled'
                        help={null}
                        className="mb-0"
                        valuePropName="checked"
                        getValueFromEvent={(e) => {
                            setDisabled(!e)
                            return e;
                        }}
                    >
                        <Switch
                            checkedChildren="Enabled"
                            unCheckedChildren="Disabled"
                        />
                    </Form.Item>
                ) : undefined}
            >
                {type === 'api' ? (
                    <Space direction="vertical" style={{ width: '100%' }} size="large">
                        <Form.Item
                            label={<Text strong>Tenant ID</Text>}
                            name="tenantId"
                            tooltip="The unique identifier for your API tenant"
                        >
                            <Input
                                disabled={disabled}
                                placeholder="Enter tenant ID"
                                style={{ fontFamily: 'monospace' }}
                            />
                        </Form.Item>
                        <Form.Item
                            label={<Text strong>Client ID</Text>}
                            name="clientId"
                            tooltip="Only for app registrations Halcyon hosts in the Halcyon Authentication tenant"
                        >
                            <Input
                                disabled={disabled}
                                placeholder="Enter client ID"
                                style={{ fontFamily: 'monospace' }}
                            />
                        </Form.Item>
                    </Space>
                ) : (
                    <>
                        <Form.List name='hooks'>
                            {(fields, { add, remove }) => (
                                <>
                                    {
                                        (fields.map((field, idx) => (
                                            <>
                                                {Boolean(idx) && <Divider className="mt-0" />}
                                                <Row align='top' justify='space-between' gutter={16}>
                                                    <Col xs={24} sm={16} md={12}>
                                                        <Form.Item 
                                                            label={<Text strong>Event Type</Text>}
                                                            name={[field.name, 'type']}
                                                            tooltip="Select which events trigger this webhook"
                                                        >
                                                            <Select placeholder="Select event type">
                                                                <Select.Option value="*">All Events</Select.Option>
                                                                <Select.Option value="transcript">Transcript</Select.Option>
                                                                <Select.Option value="signature">Signature</Select.Option>
                                                            </Select>
                                                        </Form.Item>
                                                    </Col>
                                                    <Col xs={24} sm={8} md={12} style={{ textAlign: 'right' }}>
                                                        <Tooltip title="Remove webhook">
                                                            <Button 
                                                                danger 
                                                                icon={<DeleteOutlined />}
                                                                onClick={() => { remove(idx); }}
                                                            >
                                                                Remove
                                                            </Button>
                                                        </Tooltip>
                                                    </Col>
                                                </Row>
                                                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                                                    <Form.Item
                                                        required
                                                        rules={[
                                                            { 
                                                                required: true, 
                                                                pattern: /^https:\/\/[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/[^\s]*)?$/,
                                                                message: 'Please enter a valid HTTPS URL'
                                                            },
                                                        ]}
                                                        name={[field.name, 'endpoint']}
                                                        label={<Text strong>Webhook Endpoint</Text>}
                                                        tooltip="The HTTPS URL that will receive webhook notifications"
                                                    >
                                                        <Input 
                                                            placeholder="https://example.com/webhook"
                                                            style={{ fontFamily: 'monospace' }}
                                                        />
                                                    </Form.Item>
                                                    <Form.Item
                                                        name={[field.name, 'signature']}
                                                        label={<Text strong>Signature Key</Text>}
                                                        tooltip="Use this key to verify webhook authenticity"
                                                    >
                                                        <Input
                                                            addonAfter={
                                                                <Tooltip title="Copy signature key">
                                                                    <Copy text={form.getFieldValue(['hooks', field.name]).signature} />
                                                                </Tooltip>
                                                            }
                                                            disabled
                                                            style={{ fontFamily: 'monospace' }}
                                                        />
                                                    </Form.Item>
                                                </Space>
                                            </>
                                        )))
                                    }
                                    <Row justify='end' align='top' style={{ marginTop: '16px' }}>
                                        <Button
                                            type="dashed"
                                            icon={<PlusOutlined />}
                                            onClick={() => {
                                                add({
                                                    type: '*',
                                                    endpoint: '',
                                                    signature: getSignatureKey(),
                                                });
                                            }}
                                            style={{ width: '100%' }}
                                        >
                                            Add Webhook
                                        </Button>
                                    </Row>
                                </>
                            )}
                        </Form.List>
                    </>
                )}
                <Divider style={{ margin: '24px 0 16px' }} />
                <Row
                    justify='end'
                    align='top'
                    gutter={16}
                >
                    <Col>
                        <Button
                            size="large"
                            icon={<UndoOutlined />}
                            onClick={() => { form.resetFields(); }}
                            disabled={loading}
                        >
                            Reset Changes
                        </Button>
                    </Col>
                    <Col>
                        <Button
                            size="large"
                            htmlType="submit"
                            type="primary"
                            icon={<SaveOutlined />}
                            loading={loading}
                        >
                            Save Configuration
                        </Button>
                    </Col>
                </Row>
            </Card>
        </Form>
    );
};

export default ConfigurationFormCard;
